import React, { Dispatch, SetStateAction, useContext } from 'react';
import { UseBoolean } from 'react-hanger';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './style.module.scss';
import Button from '@components-clean/core/button';
import SignOutDialog from '@components-clean/legacy/sign-out/sign-out';
import PageNavTooltip from '@components-clean/page-nav/page-nav-tooltip';
import { ActiveItemPerLevelInfo } from '@components-clean/page-nav/types';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { PopupService } from '@utils-clean/popup-service';
import { UserContext } from '@contexts/user-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { AllRoutes } from '@utils/router/all-routes';

const PageNavFooter = ({
    isCollapsed,
    showCollapsedMenuOverlay,
    setActiveItemsInfo,
}: {
    isCollapsed: boolean;
    showCollapsedMenuOverlay: UseBoolean;
    setActiveItemsInfo: Dispatch<SetStateAction<ActiveItemPerLevelInfo>>;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userContext = useContext(UserContext);
    const user = userContext?.user;
    const openSignOut = useBoolean(false);
    const { readOnly } = useReadOnlyContext();

    const openDialog = () => {
        if (readOnly) return;
        PopupService.closePopups();
        openSignOut.setTrue();
    };

    return (
        <>
            <SignOutDialog
                open={openSignOut.value}
                onClose={() => {
                    openSignOut.setFalse();
                }}
            />
            {!isCollapsed ? (
                <div className={cn(styles.pageNavFooter, 'k-px-7 k-pt-2.5 k-pb-3')}>
                    <button
                        type="button"
                        className="k-w-full
                        k-bg-transparent k-border-0 k-cursor-pointer k-outline-none k-text-left k-mb-2 k-pr-0 k-pl-0
                        k-d-flex k-align-items-center k-justify-content-between"
                        onClick={() => {
                            if (!readOnly) {
                                setActiveItemsInfo(() => ({
                                    topLevel: { id: 'user-profile', params: undefined },
                                    secondLevel: undefined,
                                    thirdLevel: undefined,
                                }));
                                navigate(AllRoutes.path('user-profile') as string);
                            }
                        }}
                    >
                        <div className="k-d-flex k-flex-col k-min-w-0">
                            <span className={cn(styles.username, 'k-font-semibold k-min-w-0 k-text-truncate k-h-5')}>
                                {user?.firstName} {user?.lastName}
                            </span>
                            <span className="k-min-w-0 k-text-truncate k-h-5 k-align-content-center">
                                {user?.email}
                            </span>
                        </div>
                        <FontAwesomeIcon
                            data-testid="profile-icon"
                            style={{ width: 18, height: 18 }}
                            icon={['far', 'circle-user']}
                        />
                    </button>
                    <div className={cn(styles.divider, 'k-mb-2.5')} />
                    <div className="k-d-flex k-w-full k-grid-cols-1">
                        <button
                            type="button"
                            data-testid="sign-out-button"
                            className={cn(
                                styles.buttonLeftBorder,
                                'k-w-full k-bg-transparent k-border-0 k-cursor-pointer k-outline-none k-text-left',
                                'k-p-0 k-d-flex k-align-items-center k-justify-content-between',
                            )}
                            onClick={openDialog}
                        >
                            {t('common:signOut')}
                            <FontAwesomeIcon
                                data-testid="sign-out-icon"
                                className="k-ml-2"
                                style={{ width: 18, height: 18 }}
                                icon={['far', 'power-off']}
                            />
                        </button>
                    </div>
                </div>
            ) : (
                <div className={cn(styles.collapsedFooter)}>
                    <div className={cn(styles.collapsedSplitter)} />
                    <div className={cn('k-w-full', { 'k-mt-6': isCollapsed })}>
                        <PageNavTooltip title={isCollapsed ? t('common:userProfile') : ''}>
                            <Button
                                data-testid="profile-button"
                                className={cn(styles.button, {
                                    '!k-bg-transparent !k-border-none k-w-full': isCollapsed,
                                })}
                                faIcon={['far', 'circle-user']}
                                onClick={() => {
                                    if (!readOnly) {
                                        setActiveItemsInfo(() => ({
                                            topLevel: { id: 'user-profile', params: undefined },
                                            secondLevel: undefined,
                                            thirdLevel: undefined,
                                        }));
                                        navigate(AllRoutes.path('user-profile') as string);
                                        showCollapsedMenuOverlay.setFalse();
                                    }
                                }}
                            />
                        </PageNavTooltip>
                    </div>
                    <div style={{ marginTop: isCollapsed ? '26px' : '' }} className="k-w-full">
                        <PageNavTooltip title={isCollapsed ? t('common:signOut') : ''}>
                            <Button
                                data-testid="sign-out-button"
                                className={cn(styles.button, {
                                    '!k-bg-transparent !k-border-none k-w-full': isCollapsed,
                                })}
                                faIcon={['far', 'power-off']}
                                onClick={openDialog}
                            />
                        </PageNavTooltip>
                    </div>
                </div>
            )}
        </>
    );
};

export default PageNavFooter;
